import { type MutationHookOptions, useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { appLinks } from 'src/routing/appLinks';
import type {
  createGrowerProduct as CreateGrowerProductData,
  createGrowerProductFromTemplate as CreateGrowerProductFromTemplateData,
  createGrowerProductFromTemplateVariables as CreateGrowerProductFromTemplateVariables,
  createGrowerProductTemplate as CreateGrowerProductTemplateData,
  createGrowerProductTemplateVariables as CreateGrowerProductTemplateVariables,
  createGrowerProductVariables as CreateGrowerProductVariables,
} from 'types/graphql';

const CREATE_GROWER_PRODUCT_MUTATION = gql`
  mutation createGrowerProduct($input: CreateGrowerProductInput!) {
    createGrowerProduct(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_GROWER_PRODUCT_TEMPLATE_MUTATION = gql`
  mutation createGrowerProductTemplate($input: CreateBlankGrowerProductTemplateInput!) {
    createBlankGrowerProductTemplate(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_GROWER_PRODUCT_FROM_TEMPLATE_MUTATION = gql`
  mutation createGrowerProductFromTemplate($orderTemplateId: Int!) {
    createGrowerProductFromTemplate(orderTemplateId: $orderTemplateId) {
      id
      slug
    }
  }
`;

/** `onError` is the caller's failure surface (the ⌘K palette toasts); without it a failed create rejects, as before. */
export function useCreateGrowerProduct(
  options: Pick<MutationHookOptions<CreateGrowerProductData, CreateGrowerProductVariables>, 'onError'> = {}
) {
  const [createGrowerProduct] = useMutation<CreateGrowerProductData, CreateGrowerProductVariables>(
    CREATE_GROWER_PRODUCT_MUTATION,
    options
  );
  const navigate = useNavigate();

  return () => {
    createGrowerProduct({
      variables: { input: {} },
      onCompleted: ({ createGrowerProduct }) => {
        void navigate(appLinks.growerProduct({ slug: createGrowerProduct.slug }));
      },
    });
  };
}

export function useCreateGrowerProductTemplate() {
  const [createTemplate] = useMutation<CreateGrowerProductTemplateData, CreateGrowerProductTemplateVariables>(
    CREATE_GROWER_PRODUCT_TEMPLATE_MUTATION
  );
  const navigate = useNavigate();

  return (name: string) => {
    createTemplate({ variables: { input: { name } } }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createBlankGrowerProductTemplate;
      void navigate(appLinks.growerProduct({ slug }));
    });
  };
}

export function useCreateGrowerProductFromTemplate() {
  const [createFromTemplate] = useMutation<
    CreateGrowerProductFromTemplateData,
    CreateGrowerProductFromTemplateVariables
  >(CREATE_GROWER_PRODUCT_FROM_TEMPLATE_MUTATION);
  const navigate = useNavigate();

  return (orderTemplateId: number) => {
    createFromTemplate({ variables: { orderTemplateId } }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createGrowerProductFromTemplate;
      void navigate(appLinks.growerProduct({ slug }));
    });
  };
}
